import {
  closeCurrentDialog,
  FormSubmitHandler,
  openDialog,
  showNotification,
  useLocalStorage,
} from 'platform/components';

import {useCallback, useEffect, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {
  SourcingPresetResponseBody,
  useLazySourcingGetPresetQuery,
  useSourcingCreatePresetMutation,
  useSourcingGetPresetsListQuery,
  useSourcingUpdatePresetMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, useOnMount, useQueryState} from 'shared/web';

import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {
  FilterPresetForm,
  SourcingFilterPresetFormData,
} from '../components/FilterPresetForm/FilterPresetForm';
import {useFilters} from '../components/FiltersContext/FiltersContext';
import {DEFAULT_FILTERS, FiltersType} from '../types/FiltersType';
import {convertFiltersBodyToFiltersType} from '../utils/convertFiltersBodyToFiltersType';
import {convertFiltersTypeToFiltersBody} from '../utils/convertFiltersTypeToFiltersBody';

type SourcingFilterPreset = Omit<SourcingPresetResponseBody, 'filters'> & {
  filters: FiltersType;
};

export type SourcingFilterPresetsControl = {
  /** Optimistic preset id for button highlight. Flips on click, before filters load. */
  selectedFilterPresetId: string | Nullish;
  /** Settled preset id that is safe to feed into the request body. Flips together with the
   * filters once the preset load resolves, so the request body never contains a hybrid of
   * the new preset id + the previous preset's filters (which would create a junk cache key
   * and trigger a one-page fetch before the real cache hit). */
  appliedFilterPresetId: string | Nullish;
  onSelectFilterPresetId: (id: string) => void;
  onCreateFilterPreset: () => void;
  onUpdateSelectedFilterPreset: () => void;
  onResetFilterPreset: () => void;
  selectedFilterPreset: SourcingFilterPreset | Nullish;
  isOutdated: boolean;
  isEmpty: boolean;
  isLoading: boolean;
};

const useInitialFilterPresetId = () =>
  useLocalStorage<string | null>('dms--sourcing-filter-preset-id', null);

export function useSourcingFilterPresets(filterModalOpened: boolean): SourcingFilterPresetsControl {
  const {filters, onChange} = useFilters();
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const sourcingType = useSourcingType();
  const [queryFilterPresetId, , clearQueryFilterPresetId] = useQueryState('filterPresetId');
  // selectedFilterPresetId is for fast interaction after click on button
  const [selectedFilterPresetId, setSelectedFilterPresetId] = useState<string | Nullish>();

  // appliedFilterPresetId only flips once filters from the preset have actually loaded, so the
  // request body and cache key never see a (newPresetId + oldFilters) hybrid mid-transition.
  const [appliedFilterPresetId, setAppliedFilterPresetId] = useState<string | Nullish>();

  // selectedFilterPreset is filter preset loaded from api after click on button
  const [selectedFilterPreset, setSelectedFilterPreset] = useState<
    SourcingFilterPreset | Nullish
  >();

  const [savedFilterPresetId, saveFilterPresetId] = useInitialFilterPresetId();

  const {data: presetsList} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });

  const [loadPreset, {isFetching}] = useLazySourcingGetPresetQuery();

  const resetFilterPreset = useCallback(() => {
    setSelectedFilterPresetId(null);
    setAppliedFilterPresetId(null);
    setSelectedFilterPreset(null);
    saveFilterPresetId(null);
  }, [saveFilterPresetId]);

  const loadFilterPreset = useCallback(
    (presetId: string, options?: {saveToLocalStorage?: boolean; clearQuery?: boolean}) => {
      setSelectedFilterPresetId(presetId);
      // preferCacheValue: skip the server roundtrip on A→B→A switches; tag-based invalidation
      // on update/duplicate keeps the cache from going stale.
      loadPreset({id: presetId}, true)
        .unwrap()
        .then((filterPreset) => {
          // Flip filters and the cache-key preset id together in one render so the request
          // body transitions straight from old-preset to new-preset, never through a hybrid.
          setSelectedFilterPreset({
            ...filterPreset,
            filters: convertFiltersBodyToFiltersType(filterPreset.filters),
          });
          onChange(convertFiltersBodyToFiltersType(filterPreset.filters));
          setAppliedFilterPresetId(presetId);
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(filterPreset.id);
          }
        })
        .catch(() => {
          setSelectedFilterPresetId(null);
          setAppliedFilterPresetId(null);
          setSelectedFilterPreset(null);
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(null);
          }
        })
        .finally(() => {
          if (options?.clearQuery) {
            clearQueryFilterPresetId();
          }
        });
    },
    [loadPreset, onChange, saveFilterPresetId, clearQueryFilterPresetId]
  );

  // Load filter preset from local storage on mount (only if no query parameter)
  useOnMount(() => {
    if (!queryFilterPresetId && savedFilterPresetId) {
      loadFilterPreset(savedFilterPresetId, {saveToLocalStorage: false});
    }
  });

  // Load filter preset when query parameter changes (or on mount if query parameter exists)
  useEffect(() => {
    if (queryFilterPresetId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadFilterPreset(queryFilterPresetId, {saveToLocalStorage: true, clearQuery: true});
    }
  }, [queryFilterPresetId, loadFilterPreset]);

  const isOutdated =
    isNotNil(selectedFilterPreset) &&
    !equals(filters, {
      ...DEFAULT_FILTERS,
      ...selectedFilterPreset?.filters,
    });

  /* Unselect filter preset when:
   - selected filters !== filters from filter preset
   - and (filters change when filter modal is closed
          - or on filter modal close) */
  useEffect(() => {
    if (!filterModalOpened && isOutdated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      resetFilterPreset();
    }
  }, [filterModalOpened, isOutdated, resetFilterPreset]);

  const [createPreset] = useSourcingCreatePresetMutation();
  const [updatePreset] = useSourcingUpdatePresetMutation();

  const onSelectFilterPresetId = (filterPresetId: string) => {
    if (filterPresetId === selectedFilterPresetId) {
      resetFilterPreset();
      return;
    }

    loadFilterPreset(filterPresetId, {saveToLocalStorage: true});
  };

  const onCreateFilterPreset = () => {
    const onSubmit: FormSubmitHandler<SourcingFilterPresetFormData> = (values) =>
      createPreset({
        createPresetRequestBody: {
          name: values.name,
          allowNotifications: values.allowNotifications ?? false,
          filters: convertFiltersTypeToFiltersBody(filters),
        },
      })
        .unwrap()
        .then((createdFilterPreset) => {
          closeCurrentDialog();
          onSelectFilterPresetId(createdFilterPreset.id);
        })
        .catch(handleApiError);

    openDialog(
      <FilterPresetForm
        initialValues={{
          name: selectedFilterPreset?.name ?? '',
          allowNotifications: sourcingType === SourcingType.AUCTIONS ? false : true,
        }}
        onSubmit={onSubmit}
      />,
      {title: i18n.t('entity.sourcing.filterPresets.labels.create')}
    );
  };

  const onUpdateSelectedFilterPreset = () => {
    if (!selectedFilterPresetId) {
      return;
    }

    return updatePreset({
      id: selectedFilterPresetId,
      updatePresetRequestBody: {
        filters: convertFiltersTypeToFiltersBody(filters),
      },
    })
      .unwrap()
      .then(() => {
        setSelectedFilterPreset((prev) => (isNotNil(prev) ? {...prev, filters} : prev));
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.success'));
      })
      .catch(handleApiError);
  };

  return {
    selectedFilterPresetId,
    appliedFilterPresetId,
    onSelectFilterPresetId,
    onCreateFilterPreset,
    onUpdateSelectedFilterPreset,
    onResetFilterPreset: resetFilterPreset,
    selectedFilterPreset,
    isOutdated,
    isEmpty: (presetsList?.length ?? 0) === 0,
    isLoading: isFetching,
  };
}
